/**
 * Extrai largura/altura nativos de um arquivo de vídeo via `<video>` temp.
 * Usa `loadedmetadata` (rápido — só baixa moov atom). Object URL é
 * revogado após o resultado pra liberar memória.
 *
 * Caller usa pra passar `width`/`height` ao POST /media/upload — backend
 * persiste em media_files e frontend reserva aspect-ratio na bolha outbound
 * desde o primeiro render.
 *
 * Rejeita em erro de carga (codec não suportado pelo browser, file corrompido).
 */
export function extractVideoDimensions(
  file: File | Blob,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const url = URL.createObjectURL(file);

    const cleanup = (): void => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    video.onloadedmetadata = (): void => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      cleanup();
      if (w > 0 && h > 0) {
        resolve({ width: w, height: h });
      } else {
        reject(new Error('Vídeo sem dimensões legíveis'));
      }
    };

    video.onerror = (): void => {
      cleanup();
      reject(new Error('Falha ao carregar metadados do vídeo'));
    };

    video.src = url;
  });
}

const POSTER_MAX_DIM = 320;

/**
 * Extrai dimensões + 1ª frame (poster) de um vídeo client-side.
 *
 * O poster vira `media_thumbnail_url` na bolha optimistic do composer —
 * vídeo outbound nasce JÁ com preview (zero flash de "preto → thumb").
 * A URL HMAC real (gerada server-side via ffmpeg) chega depois via socket/
 * refetch e substitui transparentemente.
 *
 * Poster é best-effort: se o seek/canvas falhar (CORS, codec, frame preto),
 * resolve com `poster: null` mas sempre com as dimensões (que vêm de
 * loadedmetadata, mais robusto). Nunca rejeita por causa do poster.
 */
export function extractVideoPreview(
  file: File | Blob,
): Promise<{ width: number; height: number; poster: string | null }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    // crossOrigin não importa (object URL é same-origin), mas garante canvas
    // "untainted" caso o browser seja estrito.
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    let width = 0;
    let height = 0;
    let settled = false;

    const cleanup = (): void => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    const finish = (poster: string | null): void => {
      if (settled) return;
      settled = true;
      cleanup();
      if (width > 0 && height > 0) {
        resolve({ width, height, poster });
      } else {
        reject(new Error('Vídeo sem dimensões legíveis'));
      }
    };

    const capturePoster = (): void => {
      try {
        const scale = Math.min(1, POSTER_MAX_DIM / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        finish(dataUrl.length > 64 ? dataUrl : null);
      } catch {
        finish(null);
      }
    };

    video.onloadedmetadata = (): void => {
      width = video.videoWidth;
      height = video.videoHeight;
      if (!(width > 0 && height > 0)) {
        finish(null);
        return;
      }
      // Seek pra ~0.1s — muitos vídeos têm frame preto exatamente em 0.
      try {
        video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
      } catch {
        capturePoster();
      }
    };

    video.onseeked = (): void => {
      capturePoster();
    };

    video.onerror = (): void => {
      // Sem metadata → não temos dimensões → rejeita (igual extractVideoDimensions).
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Falha ao carregar metadados do vídeo'));
    };

    // Guarda contra seek que nunca dispara seeked (alguns codecs): captura
    // o que tiver após 1.2s.
    setTimeout(() => {
      if (!settled && width > 0) capturePoster();
    }, 1200);

    video.src = url;
  });
}
